import {
  PropsWithChildren,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from "react";
import cx from "classnames";
import { Box, ScrollArea } from "@radix-ui/themes";
import styles from "./PageLayout.module.scss";

type Mode = "partial" | "full";

function getNearestScrollViewport(from: HTMLElement): HTMLElement | null {
  // Radix Themes ScrollArea viewport carries this data-attr
  const radixViewport = from.closest<HTMLElement>(
    "[data-radix-scroll-area-viewport]"
  );
  if (radixViewport) return radixViewport;

  // Fallback: nearest ancestor that actually scrolls
  let el: HTMLElement | null = from.parentElement;
  while (el) {
    const cs = getComputedStyle(el);
    const overflowY = cs.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return null;
}

export function useVisibleInNearestScrollArea(opts?: {
  mode?: Mode;
  minRatio?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [ratio, setRatio] = useState(0);

  const mode = opts?.mode ?? "partial";
  const minRatio = opts?.minRatio ?? 0.1;

  useEffect(() => {
    const target = ref.current;
    if (!target) return;

    const root = getNearestScrollViewport(target);

    // --- IntersectionObserver path ---
    let io: IntersectionObserver | null = null;
    if ("IntersectionObserver" in window) {
      // Choose thresholds so we fire near the boundary we care about
      const thresholds = mode === "full" ? [1] : [0, minRatio, 1];

      io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          setRatio(e.intersectionRatio);
          setInView(
            mode === "full"
              ? e.intersectionRatio >= 1
              : e.intersectionRatio >= minRatio
          );
        },
        { root, threshold: thresholds }
      );
      io.observe(target);
    }

    // --- Fallback (and ensures initial state) ---
    let rafId = 0;
    const compute = () => {
      if (!target) return;
      const viewportEl = root ?? document.documentElement; // window viewport
      const tRect = target.getBoundingClientRect();
      const vRect = (
        root ? root : document.documentElement
      ).getBoundingClientRect();

      const visiblePx =
        Math.min(tRect.bottom, vRect.bottom) - Math.max(tRect.top, vRect.top);
      const h = Math.max(1, tRect.height);
      const r = Math.max(0, visiblePx) / h;

      setRatio(r);
      setInView(mode === "full" ? r >= 1 : r >= minRatio);
    };

    const reflow = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };

    const scrollEl: HTMLElement | Window = root ?? window;
    const resizeEl: Window = window;

    // Always attach this minimal listener so state is correct immediately
    scrollEl.addEventListener("scroll", reflow, { passive: true });
    resizeEl.addEventListener("resize", reflow);
    reflow();

    return () => {
      io?.disconnect();
      scrollEl.removeEventListener("scroll", reflow as any);
      resizeEl.removeEventListener("resize", reflow);
      cancelAnimationFrame(rafId);
    };
  }, [mode, minRatio]);

  return { ref, inView, ratio };
}

interface PageLayoutProps {
  variant?: "v1" | "v2";
  padded?: boolean;
}

export const PageLayout = forwardRef(function PageLayout(
  {
    children,
    variant = "v1",
    padded = false,
  }: PropsWithChildren<PageLayoutProps>,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div
      className={cx(styles.pageLayout, styles[variant], {
        [styles.padded]: padded,
      })}
    >
      <ScrollArea scrollbars="vertical" style={{ height: "100%" }}>
        <div className={styles.pageContainer} ref={ref}>
          {children}
        </div>
      </ScrollArea>
    </div>
  );
});

interface PageLayoutV2Props {
  className?: string;
}

export function PageLayoutV2({
  children,
  className,
}: PropsWithChildren<PageLayoutV2Props>) {
  const { ref, inView, ratio } = useVisibleInNearestScrollArea({
    mode: "full", // or "full"
    minRatio: 0.1, // 10% visible counts as "in view"
  });

  return (
    <Box className={cx(styles.pageLayoutV2, className)}>
      <ScrollArea style={{ height: "100%", position: "relative" }}>
        {children}
      </ScrollArea>
    </Box>
  );
}
